import { Dispatch } from 'redux';
import { RootState } from '../configureStore';
import { ApplicationService } from '../../services/ApplicationService';
import { EnvironmentMode } from '../../contracts/application';
import { 
    setLoading, 
    setError, 
    setApplications,
    setScopes,
    addApplication,
    updateApplication,
    removeApplication,
    setCredentialsBeingCreated
} from './reducer';
import { toFriendlyMessage } from '../../utils/errorUtils';

export const fetchApplicationsThunk = (userId: string) => {
    return async (dispatch: Dispatch, getState: () => RootState) => {
        try {
            dispatch(setLoading(true));
            
            const state = getState().Applications;
            const environment = state.environment === 'sandbox' 
                ? EnvironmentMode.sandbox 
                : EnvironmentMode.production;
            
            const [apps, scopes] = await Promise.all([
                ApplicationService.getApplications(userId, environment),
                ApplicationService.getScopes()
            ]);
            
            dispatch(setApplications(apps));
            dispatch(setScopes(scopes));
            dispatch(setCredentialsBeingCreated(false));
        } catch (error) {
            dispatch(setError(toFriendlyMessage(error, 'Unable to load applications', 'ApplicationThunk')));
        }
    };
};

export const createApplicationThunk = (userId: string, name: string, scopeIds: string[]) => {
    return async (dispatch: Dispatch, getState: () => RootState) => {
        try {
            const state = getState().Applications;
            const scopes = state.scopes.filter(s => scopeIds.includes(s.id));
            const environment = state.environment === 'sandbox' 
                ? EnvironmentMode.sandbox 
                : EnvironmentMode.production;
            
            const newApp = await ApplicationService.createApplication(userId, {
                name,
                environment,
                scopes,
                secrets: [],
            } as any);
            
            // TODO: Replace mock with actual response flag from API
            // The API will return a boolean flag indicating if credentials are ready
            const credentialsReady = (newApp as any).credentialsReady ?? true;

            if (credentialsReady) {
                dispatch(setCredentialsBeingCreated(false));
                dispatch(addApplication(newApp));
            } else {
                dispatch(setCredentialsBeingCreated(true));
                dispatch(addApplication(newApp));
            }
        } catch (error) {
            const friendly = toFriendlyMessage(error, 'Unable to create application', 'ApplicationThunk');
            dispatch(setError(friendly));
            const friendlyError = new Error(friendly);
            (friendlyError as any).cause = error;
            throw friendlyError;
        }
    };
};

export const deleteApplicationThunk = (userId: string, applicationId: string) => {
    return async (dispatch: Dispatch, getState: () => RootState) => {
        try {
            await ApplicationService.deleteApplication(userId, applicationId);
            dispatch(removeApplication(applicationId));
        } catch (error) {
            const friendly = toFriendlyMessage(error, 'Unable to delete application', 'ApplicationThunk');
            dispatch(setError(friendly));
            const friendlyError = new Error(friendly);
            (friendlyError as any).cause = error;
            throw friendlyError;
        }
    };
};

export const renameApplicationThunk = (userId: string, applicationId: string, newName: string) => {
    return async (dispatch: Dispatch, getState: () => RootState) => {
        try {
            const state = getState().Applications;
            const currentApp = state.applications.byId[applicationId];
            
            if (!currentApp) {
                throw new Error('Application not found');
            }
            
            const updatedApp = await ApplicationService.updateApplication(userId, applicationId, {
                ...currentApp,
                name: newName.trim(),
            });
            
            dispatch(updateApplication({ ...currentApp, name: newName.trim() }));
        } catch (error) {
            const friendly = toFriendlyMessage(error, 'Unable to rename application', 'ApplicationThunk');
            dispatch(setError(friendly));
            const friendlyError = new Error(friendly);
            (friendlyError as any).cause = error;
            throw friendlyError;
        }
    };
};

export const createSecretThunk = (userId: string, applicationId: string, secretName: string, expirationDate: string) => {
    return async (dispatch: Dispatch, getState: () => RootState) => {
        try {
            const state = getState().Applications;
            const newSecret = await ApplicationService.createSecret(userId, applicationId, {
                name: secretName,
                endAt: new Date(expirationDate).toISOString(),
            } as any);
            
            const app = state.applications.byId[applicationId];
            
            if (app) {
                const updatedApp = {
                    ...app,
                    secrets: [...app.secrets, newSecret]
                };
                dispatch(updateApplication(updatedApp));
            }
        } catch (error) {
            const friendly = toFriendlyMessage(error, 'Unable to create secret', 'ApplicationThunk');
            dispatch(setError(friendly));
            const friendlyError = new Error(friendly);
            (friendlyError as any).cause = error;
            throw friendlyError;
        }
    };
};

export const deleteSecretThunk = (userId: string, applicationId: string, secretId: string) => {
    return async (dispatch: Dispatch, getState: () => RootState) => {
        try {
            const state = getState().Applications;
            await ApplicationService.deleteSecret(userId, applicationId, secretId);
            
            const app = state.applications.byId[applicationId];
            
            if (app) {
                const updatedApp = {
                    ...app,
                    secrets: app.secrets.filter(s => s.id !== secretId)
                };
                dispatch(updateApplication(updatedApp));
            }
        } catch (error) {
            const friendly = toFriendlyMessage(error, 'Unable to delete secret', 'ApplicationThunk');
            dispatch(setError(friendly));
            const friendlyError = new Error(friendly);
            (friendlyError as any).cause = error;
            throw friendlyError;
        }
    };
};

export const toggleScopeThunk = (userId: string, applicationId: string, scopeId: string, isAdding: boolean) => {
    return async (dispatch: Dispatch, getState: () => RootState) => {
        try {
            const state = getState().Applications;
            const app = state.applications.byId[applicationId];
            
            if (!app) {
                throw new Error('Application not found');
            }
            
            if (isAdding) {
                await ApplicationService.addScope(userId, applicationId, scopeId);
                const scope = state.scopes.find(s => s.id === scopeId);
                if (scope) {
                    const updatedApp = {
                        ...app,
                        scopes: [...app.scopes, scope]
                    };
                    dispatch(updateApplication(updatedApp));
                }
            } else {
                await ApplicationService.removeScope(userId, applicationId, scopeId);
                const updatedApp = {
                    ...app,
                    scopes: app.scopes.filter(s => s.id !== scopeId)
                };
                dispatch(updateApplication(updatedApp));
            }
        } catch (error) {
            const friendly = toFriendlyMessage(error, 'Unable to update scope', 'ApplicationThunk');
            dispatch(setError(friendly));
            const friendlyError = new Error(friendly);
            (friendlyError as any).cause = error;
            throw friendlyError;
        }
    };
};
